import _, { capitalize } from "lodash";

import {
  ModalRecordItem,
  ModalSection,
  ModalSubItem,
} from "../components/ApplicantDetailModal";

import { NOT_EMPLOYED_STATUSES } from "shared/config/constants";
import {
  getCreditHistoryData,
  getRentalHistoryData,
} from "./applicantHistoryHelpers";
import {
  getCreditApplicantSummary,
  getCriminalApplicantSummary,
  getIdentityApplicantSummary,
  getRentalApplicantSummary,
} from "./applicantStatusHelpers";

/**
 * Helper function to get the open balance for collections
 */
function getCollectionsOpenBalance(creditData: any): string {
  if (!creditData) {
    return null;
  }
  const openRecords = creditData.collections.records.filter(
    (record: any) => !record.date_closed
  );

  const totalOpenBalance = openRecords.reduce(
    (sum: number, record: any) => sum + (Number(record.currentBalance) || 0),
    0
  );

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(totalOpenBalance);
}

// Safely parse and format the verification type
const getFormattedVerificationType = (type: string): string => {
  try {
    if (!type || typeof type !== "string") return "N/A";
    const parts = type.split("/");
    if (parts.length < 2) return "N/A";

    const typePart = parts[1];
    if (!typePart) return "N/A";

    return typePart
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  } catch (error) {
    return "N/A";
  }
};

// Helper function to determine status for verification sub-items
const getVerificationSubItemStatus = (
  overallStatus: string,
  verificationStatus: string
): string | undefined => {
  return (overallStatus === "warn" || overallStatus === "error") &&
    verificationStatus !== "Passed"
    ? overallStatus
    : undefined;
};

/**
 * Generate modal details for ID Verification
 */
export const getIdVerificationModalDetails = (
  legacyApp: any,
  application: any,
  status: string
): ModalSection[] => {
  const { documentDetails } = getIdentityApplicantSummary(legacyApp);

  // Prepare sub-items for individual verifications
  const verificationSubItems = [];

  // Iterate through all idVerifications and create sub-items
  if (application?.idVerifications && application.idVerifications.length > 0) {
    application.idVerifications.forEach((idVerification, index) => {
      const verificationType = idVerification?.type;
      const verificationStatus =
        capitalize(idVerification?.attributes?.status) || "Unknown";
      const verificationTypeLabel =
        getFormattedVerificationType(verificationType);

      // Create descriptive message based on status
      const message = `${verificationTypeLabel} (${verificationStatus})`;

      verificationSubItems.push({
        label: `Verification ${index + 1}`,
        value: `${message}`,
        status: getVerificationSubItemStatus(status, verificationStatus),
      });
    });
  }

  return [
    {
      items: [
        {
          label: "Verification Status",
          value: legacyApp?.identity?.status || "Pending",
          status: status === "warn" || status === "error" ? status : undefined,
          subItems:
            verificationSubItems.length > 0 ? verificationSubItems : undefined,
        },
        {
          label: "Document Type",
          value:
            documentDetails !== "Pending" && documentDetails !== "Skipped"
              ? documentDetails
              : "N/A",
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Income
 */
export const getIncomeModalDetails = (
  legacyApp: any,
  incomeDisplay: string,
  balanceDisplay: string,
  incomeStatus: string
): ModalSection[] => {
  // Get employment information
  const employments = _.get(legacyApp, "snapshot.employments", []);
  const employmentStatus = _.get(legacyApp, "income.employmentStatus");
  const skippedIncome = _.get(legacyApp, "income.skipped", false);
  const income = _.get(legacyApp, "income.income", 0);
  const balance = _.get(legacyApp, "balance.balance", 0);

  // Determine auto-linked employers
  const autoLinkedEmployers = employments.filter((emp) => emp.autoLinked);
  const currentAutoLinkedEmployers = autoLinkedEmployers.filter(
    (emp) => !emp.pastEmployer
  );
  const hasAutoLinkedEmployment = currentAutoLinkedEmployers.length > 0;
  const hasAutoLinkedNotCurrentEmployments =
    autoLinkedEmployers.length > 0 && currentAutoLinkedEmployers.length === 0;

  // Determine what to display
  const showIncomeStreams = !hasAutoLinkedEmployment && employments.length > 0;
  const noIncomeVerificationAvailable =
    !hasAutoLinkedEmployment && !employments.length;

  // Create message for skipped income
  let employerMessage = "";
  if (skippedIncome) {
    employerMessage = NOT_EMPLOYED_STATUSES.includes(employmentStatus)
      ? "The applicant stated that they have never been employed"
      : "The applicant skipped income verification";
  } else if (hasAutoLinkedNotCurrentEmployments) {
    employerMessage = "The applicant did not link any current companies";
  } else if (noIncomeVerificationAvailable) {
    employerMessage = "Applicant did not link a company";
  } else if (showIncomeStreams) {
    employerMessage = "Manually entered employers";
  }

  // Format employer names for display
  const formattedEmployers =
    employments.length > 0
      ? employments
          .map((emp) => {
            return `${emp.employer}`;
          })
          .join(", ")
      : employerMessage || "None";

  return [
    {
      items: [
        {
          label:
            income > 0 || incomeStatus === "pending"
              ? "Verified Income"
              : undefined,
          value: incomeDisplay,
          status:
            incomeStatus === "warn" || incomeStatus === "error"
              ? incomeStatus
              : undefined,
        },
        {
          label:
            balance > 0 || incomeStatus === "pending"
              ? "Verified Balance"
              : undefined,
          value: balanceDisplay,
          status:
            incomeStatus === "warn" || incomeStatus === "error"
              ? incomeStatus
              : undefined,
        },
        {
          label: "Employers",
          value: formattedEmployers,
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Credit History
 */
export const getCreditHistoryModalDetails = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  isLowCreditScore: boolean,
  isThinCreditFile: boolean,
  creditScoreStatus: string,
  bankruptciesStatus: string,
  collectionsStatus: string,
  foreclosuresStatus: string
): ModalSection[] => {
  const { creditScore, bankruptcies, collections, foreclosures } =
    getCreditApplicantSummary(
      applicantLumenResult,
      application,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      isThinCreditFile,
      isLowCreditScore
    );

  const { creditScoreSummary } = application?.backgroundCreditCheck || {};

  // Get detailed credit history data if available
  const creditData = getCreditHistoryData(
    applicantLumenResult,
    creditScoreSummary
  );

  // Format bankruptcy records
  const bankruptcyRecords: ModalRecordItem[] = [];
  if (creditData?.bankruptcies?.records) {
    creditData.bankruptcies.records
      .filter((record: any) => record.date)
      .forEach((record: any) => {
        bankruptcyRecords.push({
          text: `Date: ${record.date}`,
        });
      });
  }

  // Format foreclosure records
  const foreclosureRecords: ModalRecordItem[] = [];
  if (creditData?.foreclosures?.records) {
    creditData.foreclosures.records.forEach((record: any) => {
      foreclosureRecords.push({
        text: `${record.state || "Unknown State"} - ${
          record.date || "Unknown Date"
        }`,
      });
    });
  }
  // Create collection detail items
  const collectionItems: ModalSubItem[] = [
    {
      label: "Total Open Balance",
      value: getCollectionsOpenBalance(creditData),
    },
  ];

  // Add credit collection records as a subitem with records
  if (collections === "Yes" && creditData?.collections?.records) {
    collectionItems.push({
      label: "Collection Records",
      value: creditData.collections.records.length,
    });
    creditData.collections.records.forEach((record: any) => {
      collectionItems.push({
        label: "Creditor",
        value: `${record.creditorsName} (${record.comments})`,
      });
    });
  }

  return [
    {
      items: [
        {
          label: "Credit Score",
          value: creditScore,
          status: creditScoreStatus,
        },
        {
          label: "Bankruptcies",
          value: bankruptcies,
          status: bankruptciesStatus,
          subItems:
            bankruptcies === "Yes"
              ? [
                  {
                    label: "Bankruptcy Records",
                    value: `${bankruptcyRecords.length} record${
                      bankruptcyRecords.length !== 1 ? "s" : ""
                    }`,
                    records: bankruptcyRecords,
                  },
                ]
              : undefined,
        },
        {
          label: "Collections",
          value: collections,
          status: collectionsStatus,
          subItems: collections === "Yes" ? collectionItems : undefined,
        },
        {
          label: "Foreclosures",
          value: foreclosures,
          status: foreclosuresStatus,
          subItems:
            foreclosures === "Yes"
              ? [
                  {
                    label: "Foreclosure Records",
                    value: `${foreclosureRecords.length} record${
                      foreclosureRecords.length !== 1 ? "s" : ""
                    }`,
                    records: foreclosureRecords,
                  },
                ]
              : undefined,
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Rental History
 */
export const getRentalHistoryModalDetails = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  evictionsStatus: string,
  rentalCollectionsStatus: string
): ModalSection[] => {
  const { evictionRecords, rentalCollections } = getRentalApplicantSummary(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped
  );

  const { evictionRecords: applicantEvictionRecords = {} } =
    application?.backgroundCreditCheck || {};
  // Get detailed rental history data if available
  const rentalData = getRentalHistoryData(
    applicantLumenResult,
    applicantEvictionRecords?.evictionRecordGroup
  );

  // Create items for each eviction record
  const evictionRecordItems = [];

  if (rentalData?.evictionRecords?.records) {
    evictionRecordItems.push({
      label: "Eviction Records",
      value: rentalData?.evictionRecords?.records?.length,
    });
    // Process each eviction record as its own item with subitems
    rentalData.evictionRecords.records.forEach((record, index) => {
      // Add this eviction as a main item with subitems
      evictionRecordItems.push({
        label: `Record ${index + 1}`,
        value: `${record.state || "Unknown"} (${
          record.startDate || "Unknown"
        })`,
      });
    });
  }

  return [
    {
      items: [
        {
          label: "Eviction Records",
          value: evictionRecords,
          status: evictionsStatus,
          subItems: evictionRecords === "Yes" ? evictionRecordItems : undefined,
        },
        {
          label: "Rental Collections",
          value: rentalCollections,
          status: rentalCollectionsStatus,
          //   subItems:
          //     rentalCollections === "Yes" ? rentalCollectionItems : undefined,
        },
      ],
    },
  ];
};

/**
 * Generate modal details for Criminal History
 */
export const getCriminalHistoryModalDetails = (
  application: any,
  isBackgroundCreditCheckSkipped: boolean
): ModalSection[] => {
  const { criminalRecords } = getCriminalApplicantSummary(
    application,
    isBackgroundCreditCheckSkipped
  );
  const { backgroundCreditCheck = {} } = application || {};
  const { criminalRecords: applicantCriminalRecords } =
    backgroundCreditCheck || {};

  // Create items for each record group as its own section
  const recordGroupItems = [];

  // Check if we have records in the new format with nested records
  if (applicantCriminalRecords?.criminalRecordGroup) {
    recordGroupItems.push({
      label: "Criminal Records",
      value: applicantCriminalRecords.criminalRecordGroup.length,
    });
    // For each record group, create a separate item with sub-items
    applicantCriminalRecords.criminalRecordGroup.forEach(
      (recordGroup, index) => {
        // Create sub-items for this record group
        const recordSubItems: ModalRecordItem[] = [];

        // Add charges for this record group
        if (recordGroup.records && recordGroup.records.length > 0) {
          // If multiple records exist in this group, combine their charges
          if (recordGroup.records.length > 1) {
            const chargeTexts = recordGroup.records
              .map((record) => record.chargeText)
              .filter((charge) => !!charge)
              .join(", ");

            recordSubItems.push({
              text: `Charges: ${chargeTexts || "Unknown charges"}`,
            });
          } else {
            // Single record case
            const record = recordGroup.records[0];
            recordSubItems.push({
              text: `Charge: ${record.chargeText || "Unknown charge"}`,
            });
          }

          // Add severity for the first record
          const primaryRecord = recordGroup.records[0];
          if (primaryRecord.severity) {
            recordSubItems.push({
              text: `Severity: ${primaryRecord.severity}`,
            });
          }

          // Add outcome for the first record
          if (primaryRecord.outcome) {
            recordSubItems.push({
              text: `Outcome: ${primaryRecord.outcome}`,
            });
          }
        }

        // Add this record group as a main item with its details as sub-items
        recordGroupItems.push({
          label: `Record ${index + 1}`,
          value: `${recordGroup.state || "Unknown"} (${
            recordGroup.startDate || "Unknown Date"
          })`,
          records: recordSubItems,
        });
      }
    );
  }

  return [
    {
      items: [
        {
          label: "Criminal Records",
          value: criminalRecords,
          subItems: criminalRecords === "Yes" ? recordGroupItems : undefined,
        },
      ],
    },
  ];
};
